import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import { debounce } from "lodash";
import useFetchAreasSearch from "src/Pages/Company/OrganizationStructure/api/useFetchAreasSearch";
import useOrgchartStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Utils/store";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import __ from "src/utils/Translations";
import getNoOptionsText from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Components/Autocomplete/Utils/getNoOptionsText";
import handleScroll from "./Utils/handleScroll";
import TextField from "./shared/TextField";
import AutocompleteOption from "./shared/AutocompleteOption";
import { TRenderOptionProps } from "./shared/AutocompleteOption/AutocompleteOption.models";

interface IArea {
  areaId: string;
  name: string;
  manager: { name: string; avatar?: string; initials?: string };
  structure: { id: string; label: string };
}

interface IOrgchartAutocompleteProps {
  name: string;
  label: string;
  required?: boolean;
  onSubmit: () => void;
}

const AreaAutocomplete: FC<IOrgchartAutocompleteProps> = ({
  name,
  label,
  required = false,
  onSubmit,
}) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const structureId = useStructureStore((state) => state.structureId);
  const companyId = useCompanyId();
  const { control, resetField } = useFormContext();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { setSearchedNodeId, setWasSearchedNodeCleared } = useOrgchartStore();
  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetching,
    isLoading,
  } = useFetchAreasSearch({
    companyId,
    structureId,
    filterString: search,
    enabled: isOpen && search.length >= 3,
  });

  const getOptions = useCallback(() => {
    let options: IArea[] | [] = [];
    if (isOpen && search === "") {
      return options;
    }

    if (isOpen && search.length >= 3) {
      options =
        data?.pages.flatMap((page) =>
          page.content.map((area) => ({
            areaId: area.areaId,
            name: area.name,
            manager: area.manager,
            structure: area.structure,
          }))
        ) || [];
    }

    return options;
  }, [isOpen, search, data?.pages]);

  const debouncedSearch = useRef(
    debounce((__: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 1000)
  ).current;

  const handleSearchChange = useCallback(debouncedSearch, [debouncedSearch]);

  const handleClear = () => {
    resetField(name, {
      defaultValue: "",
    });
    setIsOpen(false);
    setInputValue("");
    setSearch("");
    useOrgchartStore.getState().setSearchedNodeId(null);
    useOrgchartStore.getState().setWasSearchedNodeCleared(true);
  };

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, ref: fieldRef, value },
        fieldState: { invalid, error },
      }) => (
        <Autocomplete
          open={isOpen}
          filterOptions={(disable) => disable}
          getOptionLabel={(option) => option.name || ""}
          options={getOptions()}
          loading={isFetching || isFetchingNextPage}
          disableListWrap
          disablePortal
          noOptionsText={__(getNoOptionsText(search.length))}
          value={value}
          getOptionKey={(option) => option.areaId}
          onChange={(_event, selectedValue) => {
            onChange(selectedValue);
            onSubmit();
            if (selectedValue !== null) {
              setSearchedNodeId(selectedValue.areaId);
              setIsOpen(false);
            } else {
              setSearchedNodeId(null);
              setWasSearchedNodeCleared(true);
            }
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
            setSearch(newInputValue);
            if (
              event?.type &&
              event.type !== "click" &&
              newInputValue.length >= 3
            ) {
              setIsOpen(true);
              return handleSearchChange(event, newInputValue);
            }
            return null;
          }}
          renderInput={(params) => (
            <TextField
              params={params}
              search={search}
              inputRef={inputRef}
              isLoading={isLoading}
              isFetching={isFetching}
              isFetchingNextPage={isFetchingNextPage}
              fieldRef={fieldRef}
              handleClear={handleClear}
              invalid={invalid}
              helperText={error?.message}
              required={required}
              label={label}
            />
          )}
          renderOption={(props: TRenderOptionProps, option) => (
            <AutocompleteOption props={props} option={option} />
          )}
          slotProps={{
            listbox: {
              onScroll: (event) =>
                handleScroll({
                  event,
                  hasNextPage,
                  isFetchingNextPage,
                  fetchNextPage,
                }),
              style: {
                maxHeight: "300px",
                overflowY: "auto",
              },
            },
          }}
        />
      )}
    />
  );
};

export default AreaAutocomplete;
